import { useRef } from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
} from '@tanstack/react-query';
import { useAnalytics } from '@vaptmenu/analytics-react';
import { useAuthStore } from '@/features/auth/stores/auth-store';
import type { ApiError } from '@/shared/lib/problem-details';
import {
  assumeConversation,
  eraseContactData,
  getConversation,
  getConversationByOrder,
  getConversationMessages,
  getConversations,
  getConversationsSummary,
  getCustomerAgentStatus,
  MESSAGES_PAGE_SIZE,
  replyToConversation,
  returnConversationToAi,
  setContactDoNotServe,
  setConversationHumanOnly,
} from '../api/conversations.api';
import { apiErrorStatus } from '../lib/conversation-errors';
import { analyticsReason } from '../lib/reason-labels';
import type {
  ContactDoNotServeResponse,
  ConversationAssumeResponse,
  ConversationByOrder,
  ConversationDetail,
  ConversationHumanOnlyResponse,
  ConversationListItem,
  ConversationMessage,
  ConversationReplyKind,
  ConversationReplyResponse,
  ConversationReturnResponse,
  ConversationSummary,
  ConversationView,
  CustomerAgentStatus,
  PagedResponse,
} from '../types/conversations.types';
import { conversationsKeys, customerAgentStatusKeys } from './conversations-keys';
import { useConversationsAccess } from './useConversationsAccess';

/* ==========================================================================
   Leituras e ações do Atendimento (change customer-agent-handoff, D13 e D21)

   O que importa chega pelas campainhas do tempo real (`useOrdersSignalR`),
   com no máximo uma revalidação por segundo; a releitura periódica cobre a
   conexão caída: o resumo a cada 60 s, a conversa aberta a cada 30 s.

   As ações não têm estado otimista: no sucesso, a conversa da resposta entra
   no cache e as listas são relidas; no erro, a conversa é relida para a
   atendente ver o estado de verdade. Os eventos de analytics saem só no 2xx,
   decididos pelos campos da resposta (`changed`, `replayed`).
   ========================================================================== */

export const SUMMARY_POLL_MS = 60_000;
export const LIST_POLL_MS = 60_000;
export const CONVERSATION_POLL_MS = 30_000;
export const CUSTOMER_AGENT_STATUS_POLL_MS = 5 * 60_000;

function useTenantId(): string {
  return useAuthStore((state) => state.selectedTenant?.id ?? '');
}

function currentTenantId(): string {
  return useAuthStore.getState().selectedTenant?.id ?? '';
}

/**
 * 4xx é resposta, não falha passageira: 404 fora da área ou conversa que não
 * existe mais, 403 de token antigo ou sem o plano. Só erro de servidor ou de
 * rede tenta de novo, uma vez.
 */
function conversationsRetry(failureCount: number, error: ApiError): boolean {
  const status = apiErrorStatus(error) ?? 0;
  return (status === 0 || status >= 500) && failureCount < 1;
}

/** O resumo dos alertas: faixa, contador, card e as contagens das abas. Só com a área disponível. */
export function useConversationsSummary(enabled = true) {
  const tenantId = useTenantId();
  const { available } = useConversationsAccess();
  return useQuery<ConversationSummary, ApiError>({
    queryKey: conversationsKeys.summary(tenantId),
    queryFn: getConversationsSummary,
    enabled: enabled && available && !!tenantId,
    refetchInterval: SUMMARY_POLL_MS,
    retry: conversationsRetry,
  });
}

/** A lista de uma aba (até 50 conversas). */
export function useConversations(view: ConversationView, enabled = true) {
  const tenantId = useTenantId();
  const { available } = useConversationsAccess();
  return useQuery<PagedResponse<ConversationListItem>, ApiError>({
    queryKey: conversationsKeys.list(tenantId, view),
    queryFn: () => getConversations(view),
    enabled: enabled && available && !!tenantId,
    refetchInterval: LIST_POLL_MS,
    retry: conversationsRetry,
  });
}

/** A conversa aberta. */
export function useConversation(conversationId: string | undefined) {
  const tenantId = useTenantId();
  const { available } = useConversationsAccess();
  return useQuery<ConversationDetail, ApiError>({
    queryKey: conversationsKeys.detail(tenantId, conversationId ?? ''),
    queryFn: () => getConversation(conversationId!),
    enabled: available && !!tenantId && !!conversationId,
    refetchInterval: CONVERSATION_POLL_MS,
    retry: conversationsRetry,
  });
}

/**
 * As mensagens da conversa, 50 por página, da mais nova para a mais antiga
 * na API; as anteriores vêm sob demanda (`fetchNextPage`).
 */
export function useConversationMessages(conversationId: string | undefined) {
  const tenantId = useTenantId();
  const { available } = useConversationsAccess();
  return useInfiniteQuery<
    PagedResponse<ConversationMessage>,
    ApiError,
    InfiniteData<PagedResponse<ConversationMessage>, number>,
    readonly unknown[],
    number
  >({
    queryKey: conversationsKeys.messages(tenantId, conversationId ?? ''),
    queryFn: ({ pageParam }) => getConversationMessages(conversationId!, pageParam, MESSAGES_PAGE_SIZE),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.page < last.totalPages ? last.page + 1 : undefined),
    enabled: available && !!tenantId && !!conversationId,
    refetchInterval: CONVERSATION_POLL_MS,
    retry: conversationsRetry,
  });
}

/**
 * A linha do tempo, da mais antiga para a mais nova, sem repetir mensagem:
 * uma mensagem nova empurra as outras de página entre duas leituras.
 */
export function timelineOf(data: InfiniteData<PagedResponse<ConversationMessage>, number> | undefined): ConversationMessage[] {
  if (!data) return [];
  const seen = new Set<string>();
  const messages: ConversationMessage[] = [];
  for (const page of data.pages) {
    for (const message of page.items) {
      if (seen.has(message.messageId)) continue;
      seen.add(message.messageId);
      messages.push(message);
    }
  }
  return messages.sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt));
}

/** `GET /customer-agent/status`: o cabeçalho do Atendimento e a faixa de estado. */
export function useCustomerAgentStatus(enabled = true) {
  const tenantId = useTenantId();
  const { available } = useConversationsAccess();
  return useQuery<CustomerAgentStatus, ApiError>({
    queryKey: customerAgentStatusKeys.status(tenantId),
    queryFn: getCustomerAgentStatus,
    enabled: enabled && available && !!tenantId,
    refetchInterval: CUSTOMER_AGENT_STATUS_POLL_MS,
    retry: conversationsRetry,
  });
}

/**
 * A conversa do pedido, para o "Ver conversa" do detalhe (D13). Só pergunta
 * com a área disponível e `conversations.read`; o 404 (pedido sem conversa)
 * não tenta de novo.
 */
export function useConversationByOrder(orderId: string | undefined) {
  const tenantId = useTenantId();
  const { available } = useConversationsAccess();
  return useQuery<ConversationByOrder, ApiError>({
    queryKey: conversationsKeys.byOrder(tenantId, orderId ?? ''),
    queryFn: () => getConversationByOrder(orderId!),
    enabled: available && !!tenantId && !!orderId,
    retry: conversationsRetry,
  });
}

/* ---- Ações ---- */

/** A conversa da resposta entra no cache; listas, resumo e mensagens são relidos. */
function applyConversation(queryClient: QueryClient, conversation: ConversationDetail): void {
  const tenantId = currentTenantId();
  if (!tenantId) return;
  queryClient.setQueryData(conversationsKeys.detail(tenantId, conversation.conversationId), conversation);
  void queryClient.invalidateQueries({ queryKey: conversationsKeys.lists(tenantId) });
  void queryClient.invalidateQueries({ queryKey: conversationsKeys.summary(tenantId) });
  void queryClient.invalidateQueries({ queryKey: conversationsKeys.messages(tenantId, conversation.conversationId) });
}

/** No erro, a conversa é relida (sem estado otimista, o que vale é o que a API diz agora). */
function reloadConversation(queryClient: QueryClient, conversationId: string): void {
  const tenantId = currentTenantId();
  if (!tenantId) return;
  void queryClient.invalidateQueries({ queryKey: conversationsKeys.detail(tenantId, conversationId) });
  void queryClient.invalidateQueries({ queryKey: conversationsKeys.messages(tenantId, conversationId) });
  void queryClient.invalidateQueries({ queryKey: conversationsKeys.lists(tenantId) });
  void queryClient.invalidateQueries({ queryKey: conversationsKeys.summary(tenantId) });
}

export interface AssumeVariables {
  /** O motivo da conversa antes de assumir (vai no evento). */
  reason: string;
}

/** Assumir; `panel.conversation_assumed` sai só com `changed: true`. */
export function useAssumeConversation(conversationId: string) {
  const queryClient = useQueryClient();
  const { track } = useAnalytics();
  return useMutation<ConversationAssumeResponse, ApiError, AssumeVariables>({
    mutationFn: () => assumeConversation(conversationId),
    onSuccess: (response, { reason }) => {
      applyConversation(queryClient, response.conversation);
      if (response.changed) {
        track('panel.conversation_assumed', {
          reason: analyticsReason(reason),
          seconds_waiting: response.waitedSeconds,
        });
      }
    },
    onError: () => reloadConversation(queryClient, conversationId),
  });
}

/** Devolver para o Atendente IA; `panel.conversation_returned_to_ai` só com `changed: true`. */
export function useReturnConversationToAi(conversationId: string) {
  const queryClient = useQueryClient();
  const { track } = useAnalytics();
  return useMutation<ConversationReturnResponse, ApiError, void>({
    mutationFn: () => returnConversationToAi(conversationId),
    onSuccess: (response) => {
      applyConversation(queryClient, response.conversation);
      if (response.changed) {
        track('panel.conversation_returned_to_ai', { seconds_with_team: response.secondsWithTeam });
      }
    },
    onError: () => reloadConversation(queryClient, conversationId),
  });
}

/** Ligar e desligar "Só a equipe"; `panel.conversation_human_only_changed` só com `changed: true`. */
export function useSetConversationHumanOnly(conversationId: string) {
  const queryClient = useQueryClient();
  const { track } = useAnalytics();
  return useMutation<ConversationHumanOnlyResponse, ApiError, boolean>({
    mutationFn: (humanOnly) => setConversationHumanOnly(conversationId, humanOnly),
    onSuccess: (response, humanOnly) => {
      applyConversation(queryClient, response.conversation);
      if (response.changed) {
        track('panel.conversation_human_only_changed', { human_only: humanOnly });
      }
    },
    onError: () => reloadConversation(queryClient, conversationId),
  });
}

export interface ReplyVariables {
  kind: ConversationReplyKind;
  text?: string;
  /** A chave do envio: nova a cada envio, a mesma só na nova tentativa dele. */
  idempotencyKey: string;
}

/**
 * Responder pelo painel. `panel.conversation_reply_sent` sai no primeiro 2xx
 * recebido para cada `Idempotency-Key`, venha ele com `replayed` falso ou
 * verdadeiro: um clique duplo (o replay da mesma chave) não emite duas vezes,
 * e a nova tentativa depois de uma resposta perdida na rede emite uma vez.
 */
export function useReplyToConversation(conversationId: string) {
  const queryClient = useQueryClient();
  const { track } = useAnalytics();
  const trackedKeys = useRef(new Set<string>());
  return useMutation<ConversationReplyResponse, ApiError, ReplyVariables>({
    mutationFn: ({ kind, text, idempotencyKey }) =>
      replyToConversation(conversationId, kind === 'text' ? { kind, text } : { kind }, idempotencyKey),
    onSuccess: (_response, { kind, idempotencyKey }) => {
      const tenantId = currentTenantId();
      if (tenantId) {
        void queryClient.invalidateQueries({ queryKey: conversationsKeys.messages(tenantId, conversationId) });
        void queryClient.invalidateQueries({ queryKey: conversationsKeys.detail(tenantId, conversationId) });
        void queryClient.invalidateQueries({ queryKey: conversationsKeys.lists(tenantId) });
        void queryClient.invalidateQueries({ queryKey: conversationsKeys.summary(tenantId) });
      }
      if (!trackedKeys.current.has(idempotencyKey)) {
        trackedKeys.current.add(idempotencyKey);
        track('panel.conversation_reply_sent', { kind });
      }
    },
    onError: () => reloadConversation(queryClient, conversationId),
  });
}

/** "Não atender" pela rota de contato do canal; `panel.contact_do_not_serve_changed` no 2xx. */
export function useSetContactDoNotServe(conversationId: string) {
  const queryClient = useQueryClient();
  const { track } = useAnalytics();
  return useMutation<ContactDoNotServeResponse, ApiError, { contactId: string; doNotServe: boolean }>({
    mutationFn: ({ contactId, doNotServe }) => setContactDoNotServe(contactId, doNotServe),
    onSuccess: (response) => {
      reloadConversation(queryClient, conversationId);
      track('panel.contact_do_not_serve_changed', { do_not_serve: response.doNotServe });
    },
    onError: () => reloadConversation(queryClient, conversationId),
  });
}

/** "Apagar dados deste contato" (só `whatsapp.manage`); `panel.contact_data_erased` no 204. */
export function useEraseContactData(conversationId: string) {
  const queryClient = useQueryClient();
  const { track } = useAnalytics();
  return useMutation<void, ApiError, { contactId: string }>({
    mutationFn: ({ contactId }) => eraseContactData(contactId),
    onSuccess: () => {
      const tenantId = currentTenantId();
      if (tenantId) {
        queryClient.removeQueries({ queryKey: conversationsKeys.detail(tenantId, conversationId) });
        queryClient.removeQueries({ queryKey: conversationsKeys.messages(tenantId, conversationId) });
        void queryClient.invalidateQueries({ queryKey: conversationsKeys.lists(tenantId) });
        void queryClient.invalidateQueries({ queryKey: conversationsKeys.summary(tenantId) });
      }
      track('panel.contact_data_erased');
    },
    onError: () => reloadConversation(queryClient, conversationId),
  });
}
